<template>
    <div ref="root" class="sql-editor"></div>
</template>

<script>
/**
 * SQL 编辑器（基于 CodeMirror 5，随 @kangc/v-md-editor 已内置，无需额外依赖）
 * <p>能力：语法高亮、行号、括号匹配/自动闭合、自动缩进、关键字补全（Ctrl/Cmd+Space）、
 * Ctrl/Cmd+Enter 触发执行。</p>
 * <p>用法：`v-model` 绑定 SQL 文本，`mode` 传方言（`text/x-pgsql` / `text/x-mysql`），
 * `@run` 接收执行事件。</p>
 */
import { markRaw } from 'vue';
import CodeMirror from 'codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/mode/sql/sql.js';
import 'codemirror/addon/edit/matchbrackets.js';
import 'codemirror/addon/edit/closebrackets.js';
import 'codemirror/addon/hint/show-hint.js';
import 'codemirror/addon/hint/show-hint.css';
import 'codemirror/addon/hint/sql-hint.js';
import 'codemirror/addon/selection/active-line.js';
import 'codemirror/addon/display/placeholder.js';

/**
 * SQL 关键字分类：用于补全列表前的浅灰色标记（DDL / DML / DCL / TCL / 条件 / 函数 / 子句）。
 * 未收录的关键字走 `[SQL]` 兜底；表名统一 `[表]`、列名统一 `[列]`。
 */
const KEYWORD_CATEGORY = {
    // DDL（定义）
    CREATE: 'ddl', ALTER: 'ddl', DROP: 'ddl', TRUNCATE: 'ddl', RENAME: 'ddl', COMMENT: 'ddl',
    TABLE: 'ddl', VIEW: 'ddl', INDEX: 'ddl', SCHEMA: 'ddl', DATABASE: 'ddl', SEQUENCE: 'ddl',
    TRIGGER: 'ddl', FUNCTION: 'ddl', PROCEDURE: 'ddl', COLUMN: 'ddl', PRIMARY: 'ddl',
    FOREIGN: 'ddl', REFERENCES: 'ddl', CONSTRAINT: 'ddl', UNIQUE: 'ddl', CASCADE: 'ddl',
    TEMPORARY: 'ddl', MATERIALIZED: 'ddl', ADD: 'ddl', MODIFY: 'ddl', REPLACE: 'ddl',
    // DML（操作数据）
    SELECT: 'dml', INSERT: 'dml', UPDATE: 'dml', DELETE: 'dml', MERGE: 'dml', SET: 'dml',
    VALUES: 'dml', INTO: 'dml', UPSERT: 'dml', RETURNING: 'dml',
    // DCL（权限）
    GRANT: 'dcl', REVOKE: 'dcl', DENY: 'dcl',
    // TCL（事务）
    BEGIN: 'tcl', COMMIT: 'tcl', ROLLBACK: 'tcl', SAVEPOINT: 'tcl', TRANSACTION: 'tcl',
    RELEASE: 'tcl', START: 'tcl', WORK: 'tcl', ISOLATION: 'tcl',
    // 条件
    WHERE: 'cond', AND: 'cond', OR: 'cond', NOT: 'cond', IN: 'cond', BETWEEN: 'cond',
    LIKE: 'cond', ILIKE: 'cond', IS: 'cond', NULL: 'cond', EXISTS: 'cond', ANY: 'cond',
    ALL: 'cond', SOME: 'cond', CASE: 'cond', WHEN: 'cond', THEN: 'cond', ELSE: 'cond',
    END: 'cond', SIMILAR: 'cond', ESCAPE: 'cond',
    // 函数
    COUNT: 'func', SUM: 'func', AVG: 'func', MIN: 'func', MAX: 'func', COALESCE: 'func',
    CAST: 'func', NOW: 'func', NULLIF: 'func', CONCAT: 'func', SUBSTRING: 'func',
    TRIM: 'func', UPPER: 'func', LOWER: 'func', LENGTH: 'func', ROUND: 'func', ABS: 'func',
    EXTRACT: 'func', GREATEST: 'func', LEAST: 'func', CURRENT_DATE: 'func',
    CURRENT_TIME: 'func', CURRENT_TIMESTAMP: 'func', DATE_TRUNC: 'func',
    TO_CHAR: 'func', TO_DATE: 'func',
    // 子句
    FROM: 'clause', JOIN: 'clause', INNER: 'clause', LEFT: 'clause', RIGHT: 'clause',
    FULL: 'clause', OUTER: 'clause', CROSS: 'clause', ON: 'clause', USING: 'clause',
    GROUP: 'clause', BY: 'clause', ORDER: 'clause', HAVING: 'clause', LIMIT: 'clause',
    OFFSET: 'clause', UNION: 'clause', INTERSECT: 'clause', EXCEPT: 'clause', AS: 'clause',
    WITH: 'clause', ASC: 'clause', DESC: 'clause', DISTINCT: 'clause', NATURAL: 'clause',
    LATERAL: 'clause', OVER: 'clause', PARTITION: 'clause', WINDOW: 'clause',
    FETCH: 'clause', ROWS: 'clause'
};

/**
 * 上下文关键字：这些关键字后面通常跟「表名」，补全时把表名往上放。
 * （DESC 不列入，避免与 `ORDER BY x DESC` 混淆）
 */
const TABLE_CTX = new Set([
    'FROM', 'JOIN', 'INTO', 'UPDATE', 'TABLE', 'TRUNCATE', 'DESCRIBE', 'ALTER'
]);

/** 这些关键字后面通常跟「字段/表达式」，补全时把列名往上放 */
const COLUMN_CTX = new Set([
    'WHERE', 'ON', 'AND', 'OR', 'SELECT', 'SET', 'BY', 'HAVING', 'USING',
    'ORDER', 'GROUP', 'VALUES', 'WHEN', 'THEN', 'ELSE', 'CASE'
]);

/**
 * 判断当前光标位置的补全上下文：
 * - `table`：位于 FROM/JOIN 等关键字之后 → 优先表名
 * - `column`：位于 WHERE/ON 等关键字之后，或 `表.` 之后 → 优先列名
 * - `none`：无明确倾向，保持原顺序
 */
function detectContext(editor) {
    const before = editor.getRange({ line: 0, ch: 0 }, editor.getCursor());
    // 去掉正在输入的半截词，再看前一个词
    const trimmed = before.replace(/[A-Za-z0-9_]*$/, '').replace(/\s+$/, '');
    if (trimmed.endsWith('.')) return 'column'; // 表. → 列
    const m = trimmed.match(/([A-Za-z_][A-Za-z0-9_]*)$/);
    if (!m) return 'none';
    const word = m[1].toUpperCase();
    if (TABLE_CTX.has(word)) return 'table';
    if (COLUMN_CTX.has(word)) return 'column';
    return 'none';
}

/**
 * 分类标记文案：用 `render` 写到 `li` 的 `data-hidoc-label` 上，再由 CSS `::before` 的
 * `content: attr(data-hidoc-label)` 渲染（标记不进入插入文本、也不参与匹配）。
 * 表名 / 列名不在这里，见 {@link decorateHints}（列名要带上来源表）。
 */
const LABEL_TEXT = {
    ddl: '[DDL]', dml: '[DML]', dcl: '[DCL]', tcl: '[TCL]',
    cond: '[条件]', func: '[函数]', clause: '[子句]', sql: '[SQL]'
};

/**
 * 找出列名项来自哪张表：
 * - 条件上下文里由本组件补入的列，已带 `hidocTable`；
 * - 自带补全在 `表.` 之后产出的列，text 形如 `表名.列名`（别名场景即别名），取最后一个点之前的部分。
 */
function columnOwnerTable(item, text) {
    if (item && typeof item === 'object' && item.hidocTable) return item.hidocTable;
    const s = String(text || '');
    const dot = s.lastIndexOf('.');
    return dot > 0 ? s.slice(0, dot) : null;
}

/**
 * 分类 + 上下文排序 + 加类型标记：
 * 给补全项加分类标记（`data-hidoc-label` + CSS `::before`，标记不进入插入文本、也不参与匹配）；
 * 并根据 {@link detectContext} 把表名（FROM/JOIN 后）或列名（WHERE/ON 后）往上浮，让首选更贴近意图。
 * 列名的标记会带上来源表，形如 `[列:plu]`。
 */
function decorateHints(list, tables, context) {
    const upperTables = new Set(Object.keys(tables || {}).map((name) => name.toUpperCase()));
    const items = list.map((item, index) => {
        const isObj = !!item && typeof item === 'object';
        const text = isObj ? item.text : item;
        const baseClass = (isObj && item.className) || '';
        let kind; // keyword | table | column
        let category;
        let label;
        if (baseClass.indexOf('CodeMirror-hint-keyword') > -1) {
            kind = 'keyword';
            category = KEYWORD_CATEGORY[String(text).toUpperCase()] || 'sql';
            label = LABEL_TEXT[category];
        } else if (text && upperTables.has(String(text).toUpperCase())) {
            kind = 'table';
            category = 'table';
            label = '[表]';
        } else {
            kind = 'column';
            category = 'column';
            const owner = columnOwnerTable(item, text);
            label = owner ? '[列:' + owner + ']' : '[列]';
        }
        return { item, isObj, baseClass, text, kind, category, label, index };
    });

    // 上下文排序：FROM/JOIN 后表名优先；WHERE/ON 后列名优先（同组内保持原顺序，稳定排序）
    if (context === 'table' || context === 'column') {
        const wanted = context;
        items.sort((a, b) => {
            const ra = a.kind === wanted ? 0 : 1;
            const rb = b.kind === wanted ? 0 : 1;
            return ra - rb || a.index - b.index;
        });
    }

    return items.map(({ item, isObj, baseClass, text, category, label }) => {
        const categoryClass = 'hidoc-hint hidoc-hint--' + category;
        const className = baseClass ? baseClass + ' ' + categoryClass : categoryClass;
        const base = isObj ? Object.assign({}, item) : { text: item };
        base.text = text;
        base.className = className;
        // 自定义渲染：只写标记属性 + 文本，插入内容仍取自 item.text
        base.render = (elt) => {
            elt.setAttribute('data-hidoc-label', label);
            elt.appendChild(elt.ownerDocument.createTextNode(text));
        };
        return base;
    });
}

/**
 * 从 SQL 文本中解析「用到的表」（FROM / JOIN / UPDATE / INTO / TRUNCATE / TABLE 之后的名字）。
 * 只保留在已知表名列表里的（避免用户还没输完就发请求）；同时兼容「不带 schema 前缀」的写法。
 */
function extractUsedTables(sql, knownTables) {
    const list = knownTables || [];
    if (!list.length) return [];
    const exact = new Map(); // 大写全名 -> 规范名
    const short = new Map(); // 大写短名 -> 规范名（首个命中）
    list.forEach((t) => {
        const u = String(t).toUpperCase();
        exact.set(u, t);
        const dot = u.lastIndexOf('.');
        const s = dot >= 0 ? u.slice(dot + 1) : u;
        if (!short.has(s)) short.set(s, t);
    });
    const found = [];
    const seen = new Set();
    const re = /\b(?:from|join|update|into|truncate|table)\s+([A-Za-z_][\w$]*(?:\.[A-Za-z_][\w$]*)?)/gi;
    let m;
    while ((m = re.exec(sql)) !== null) {
        const upper = m[1].toUpperCase();
        let name = exact.get(upper);
        if (!name) {
            const dot = upper.lastIndexOf('.');
            name = short.get(dot >= 0 ? upper.slice(dot + 1) : upper);
        }
        if (!name || seen.has(name)) continue;
        seen.add(name);
        found.push(name);
    }
    return found;
}

/**
 * 注册 SQL 补全助手：在 CodeMirror 自带 sql 补全的能力之上（前缀匹配、`表.` 后补列名、别名解析、关键字），
 * 再补充「包含式」模糊匹配的表名 —— 例如输入 `user` 也能命中 `sys_user`（自带补全只做前缀匹配，命中不了）。
 * 只注册一次（helper 是全局的）。
 */
let sqlHintRegistered = false;

function registerSqlHint() {
    if (sqlHintRegistered) return;
    sqlHintRegistered = true;
    CodeMirror.registerHelper('hint', 'hidocSql', (editor, options) => {
        const opts = options || {};
        const base = CodeMirror.hint.sql(editor, opts) || {
            list: [],
            from: editor.getCursor(),
            to: editor.getCursor()
        };
        const list = base.list || [];
        const tables = opts.tables;

        // 取当前输入片段（与 sql-hint 内部取法保持一致）
        const cur = editor.getCursor();
        const token = editor.getTokenAt(cur);
        let search = token.string;
        if (token.end > cur.ch) search = search.slice(0, cur.ch - token.start);
        const first = search.charAt(0);
        // 点号 / 引号之后是「列名」场景（自带补全会处理），此时不再补充表名 / 裸列名
        const nameContext = search && first !== '.' && first !== '"' && first !== '`' && first !== "'";
        const context = detectContext(editor);

        if (tables && nameContext && list.length <= 300) {
            const upper = search.toUpperCase();
            const added = new Set(list.map((item) => {
                const text = typeof item === 'string' ? item : item.text;
                return String(text || '').toUpperCase();
            }));
            // 表名：补充「包含式」模糊匹配（自带只做前缀匹配，输入 user 命中不了 sys_user）
            Object.keys(tables).forEach((name) => {
                const key = name.toUpperCase();
                if (added.has(key) || key.indexOf(upper) === -1) return;
                list.push({ text: name, className: 'CodeMirror-hint-table' });
                added.add(key);
            });
            // 列名：自带补全只在「表.」之后给列名，条件/字段上下文（WHERE/ON/SELECT/…）里
            // 把「已按需拉到的列名」也加进来 —— 否则在 WHERE 后输入时根本看不到字段
            if (context === 'column') {
                Object.keys(tables).forEach((name) => {
                    const cols = tables[name];
                    if (!cols || !cols.length) return;
                    cols.forEach((col) => {
                        const key = String(col).toUpperCase();
                        if (added.has(key) || key.indexOf(upper) === -1) return;
                        list.push({ text: col, className: 'CodeMirror-hint-column', hidocTable: name });
                        added.add(key);
                    });
                });
            }
        }

        // 分类标记（浅灰小标签）+ 按上下文把表名/列名上浮
        base.list = decorateHints(list, tables, context);
        return base;
    });
}

/** 当前输入内容的最后一个字符是否需要触发补全（词字符 / 点号 / 引号） */
const HINT_TRIGGER = /[\w."'`]$/;

export default {
    name: 'SqlEditor',
    props: {
        modelValue: { type: String, default: '' },
        // CodeMirror 的 mode（MIME）：text/x-pgsql / text/x-mysql / text/x-sql ...
        mode: { type: String, default: 'text/x-sql' },
        readOnly: { type: Boolean, default: false },
        placeholder: { type: String, default: '' },
        /**
         * 当前库的表名列表（由父组件按「连接 + 库」拉取并缓存后传入）；
         * 为空则只做文档内词与关键字的补全。
         */
        tables: { type: Array, default: () => [] },
        /**
         * 按需拉取的列名映射：`{ 表名: [列名...] }`
         * （父组件收到本组件的 `need-columns` 事件后拉取并合入；未拉到的表为空）
         */
        columns: { type: Object, default: null }
    },
    emits: ['update:modelValue', 'run', 'need-columns'],
    data() {
        return { cm: null };
    },
    watch: {
        modelValue(val) {
            // 外部变化（如「载入」「格式化」）才回写到编辑器，避免与输入循环触发
            if (this.cm && val !== this.cm.getValue()) {
                this.cm.setValue(val || '');
            }
        },
        mode(val) {
            if (this.cm) this.cm.setOption('mode', val);
        },
        readOnly(val) {
            if (this.cm) this.cm.setOption('readOnly', val);
        },
        // 表名列表变化（打开抽屉时拉取完成、或用户切换了数据库）→ 更新补全数据，并尝试补拉列名
        tables() {
            if (this.cm) this.cm.setOption('hintOptions', this.buildHintOptions());
            this.emitUsedTables();
        },
        // 列名按需返回 → 更新补全数据
        columns() {
            if (this.cm) this.cm.setOption('hintOptions', this.buildHintOptions());
        }
    },
    mounted() {
        // 关键：CodeMirror 实例必须用 markRaw 包一层再赋值。
        // 否则它会被 Vue 3 的响应式系统包成 Proxy，而 CM 内部大量依赖对象引用相等
        // （如 mapFromLineView 里的 lineView.line == line），经 Proxy 调用后引用身份错乱，
        // 会让 charCoords/coordsChar 抛「Cannot read properties of undefined (reading 'map')」，
        // 表现为点不上光标、光标错位、输入被丢弃，且 refresh 也修不好（走的还是 Proxy）。
        registerSqlHint();
        this.cm = markRaw(CodeMirror(this.$refs.root, {
            value: this.modelValue || '',
            mode: this.mode,
            // 表名/列名补全（表名由父组件按「连接 + 库」拉取后传入，列名按需拉取）
            hintOptions: this.buildHintOptions(),
            lineNumbers: true,
            lineWrapping: true,
            indentUnit: 2,
            tabSize: 2,
            smartIndent: true,
            matchBrackets: true,
            autoCloseBrackets: true,
            styleActiveLine: true,
            readOnly: this.readOnly,
            placeholder: this.placeholder,
            extraKeys: {
                'Ctrl-Space': 'autocomplete',
                'Cmd-Space': 'autocomplete',
                'Ctrl-Enter': () => this.$emit('run'),
                'Cmd-Enter': () => this.$emit('run'),
                // Tab 插入两个空格（不跳出输入框）
                Tab: (cm) => cm.replaceSelection('  ', 'end')
            }
        }));
        this.cm.on('change', (cm) => {
            this.$emit('update:modelValue', cm.getValue());
            // 语句里用到的表可能变化 → 通知父组件按需拉取列名
            // （emitUsedTables 内部按「用到的表集合」去重，不会重复请求）
            this.emitUsedTables();
        });

        // 关掉输入域的浏览器拼写检查/自动纠正（否则输入内容后文字下方可能出现波浪线或下划线）
        const inputField = this.cm.getInputField && this.cm.getInputField();
        if (inputField) {
            inputField.setAttribute('spellcheck', 'false');
            inputField.setAttribute('autocorrect', 'off');
            inputField.setAttribute('autocapitalize', 'off');
        }

        // 输入时自动弹出补全：仅在输入词字符/点号/引号时触发，且不在字符串、注释里弹
        this.cm.on('inputRead', (cm, change) => {
            const text = (change.text && change.text[0]) || '';
            if (!text || !HINT_TRIGGER.test(text)) return;
            if (cm.state.completionActive) return;
            const token = cm.getTokenAt(cm.getCursor());
            if (token && (token.type === 'string' || token.type === 'comment')) return;
            cm.showHint();
        });

        // 初次布局可能还没稳定（抽屉/卡片入场动画），挂载后补测一次
        // （markRaw 已解决度量失效，这里只用于保证首次渲染的尺寸正确）
        this.rafId = 0;
        this.refresh();
        this.$nextTick(() => this.refresh());

        // 兜底修复 CodeMirror 的「焦点状态失同步」：
        // CM5 在「窗口失焦」时会把所有编辑器的内部 state.focused 置为 false（见 CM 的 window blur 处理），
        // 但浏览器恢复时输入域可能仍是 activeElement —— 此时 CM 与真实焦点不一致，
        // 而后 CM 的 ensureFocus 判断「已有焦点」会直接跳过、不再复位；
        // 表现为：点不进光标、看不到插入点、输入被直接丢弃，必须切到别的元素再点回来才恢复。
        // 这里在交互时校一次：只要浏览器焦点其实在输入域上，就补发一次 focus 事件让 CM 重新登记。
        this.syncFocus = () => this.repairFocus();
        ['mousedown', 'touchstart', 'keydown', 'focusin'].forEach((ev) => {
            this.$refs.root.addEventListener(ev, this.syncFocus);
        });
        // 窗口重新获得焦点时也校一次，避免 Alt+Tab 回来后光标半天不出来
        window.addEventListener('focus', this.syncFocus);
    },
    beforeUnmount() {
        if (this.syncFocus && this.$refs.root) {
            ['mousedown', 'touchstart', 'keydown', 'focusin'].forEach((ev) => {
                this.$refs.root.removeEventListener(ev, this.syncFocus);
            });
            window.removeEventListener('focus', this.syncFocus);
            this.syncFocus = null;
        }
        if (this.rafId) cancelAnimationFrame(this.rafId);
        if (this.cm) {
            const el = this.cm.getWrapperElement();
            if (el && el.parentNode) el.parentNode.removeChild(el);
            this.cm = null;
        }
    },
    methods: {
        // 取当前选中的文本（供父组件「执行选中」使用）
        getSelectedText() {
            return this.cm ? this.cm.getSelection() : '';
        },
        // 供 CodeMirror 用的表名→列名映射：表名来自 props.tables（值默认空），列名来自按需拉取的 props.columns
        buildTablesMap() {
            const map = {};
            (this.tables || []).forEach((t) => {
                if (t) map[t] = [];
            });
            const cols = this.columns || {};
            Object.keys(cols).forEach((t) => {
                map[t] = cols[t] || [];
            });
            return map;
        },
        // 补全选项：hint 用增强过的 SQL 助手；completeSingle=false 避免只匹配一项时直接插入
        buildHintOptions() {
            return {
                hint: CodeMirror.hint.hidocSql,
                tables: this.buildTablesMap(),
                completeSingle: false
            };
        },
        // 从当前内容解析「用到的表」（只保留已知表名），集合变化时 emit 给父组件按需拉列名
        emitUsedTables() {
            if (!this.cm) return;
            const found = extractUsedTables(this.cm.getValue(), this.tables);
            const sig = found.join('|');
            if (sig === this.lastEmittedTables) return;
            this.lastEmittedTables = sig;
            if (found.length) this.$emit('need-columns', found);
        },
        // 重新测量：先同步 refresh 一次（保证紧接着的点击/输入处理拿到正确度量），
        // 再排一帧兜底（覆盖「同一帧内布局仍在变化」的情况）。
        refresh() {
            if (!this.cm) return;
            this.cm.refresh();
            if (this.rafId) cancelAnimationFrame(this.rafId);
            this.rafId = requestAnimationFrame(() => {
                this.rafId = 0;
                if (this.cm) this.cm.refresh();
            });
        },
        // 校正 CM 焦点状态（详见 mounted 里的说明）
        repairFocus() {
            const cm = this.cm;
            if (!cm || cm.state.focused) return;
            const field = cm.getInputField && cm.getInputField();
            // 仅当浏览器焦点确实在输入域上、只是 CM 没登记时才补 focus，
            // 否则交给浏览器正常派发（避免抢焦点）
            if (!field || document.activeElement !== field) return;
            cm.getInputField().dispatchEvent(new Event('focus'));
        }
    }
};
</script>

<style>
.sql-editor .CodeMirror {
    height: 90px;
    border: 1px solid #dfe7f1;
    border-radius: 8px;
    background-color: #ffffff;
    font-size: 12px;
    line-height: 18px;
    /* CodeMirror 默认字体是等宽族，这里保持等宽 */
    font-family: Consolas, Menlo, Monaco, 'Courier New', monospace;
}

.sql-editor .CodeMirror-focused {
    border-color: #a9c2ee;
    box-shadow: 0 0 0 2px rgba(47, 111, 237, 0.12);
}

.sql-editor .CodeMirror-gutters {
    border-right: 1px solid #eef1f6;
    background-color: #fafbfe;
}

.sql-editor .CodeMirror-linenumber {
    color: #aab4c4;
}

.sql-editor .CodeMirror-activeline-background {
    background-color: #f5f8fe;
}

/* 补全提示框：CodeMirror 会把它挂到 body 上（不在编辑器内），所以必须用全局选择器；
   抽屉的 z-index 很高，不提权会被盖住看不见。 */
.CodeMirror-hints {
    z-index: 3000;
    font-family: Consolas, Menlo, Monaco, 'Courier New', monospace;
    font-size: 12px;
}

/* 补全项前的分类标记（浅灰、不进入插入文本、也不参与匹配）。
   文案由 JS 渲染时写到 li 的 data-hidoc-label（列名会带上来源表，如 [列:plu]）。 */
.CodeMirror-hints .hidoc-hint::before {
    content: attr(data-hidoc-label);
    display: inline-block;
    min-width: 44px;
    margin-right: 6px;
    color: #b3bcc9;
    font-size: 11px;
}

/* 选中项背景较深，标记换成半透明白以免看不清 */
.CodeMirror-hints .CodeMirror-hint-active.hidoc-hint::before {
    color: rgba(255, 255, 255, 0.75);
}
</style>
